import React, { type FC, type DOMAttributes, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';

import type { Lucid } from '../../types/types';

export interface TableWrapperProps extends DOMAttributes<HTMLElement> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The available variants for the Text component. */
  variant?: Lucid.Theme.Component.Typography.VariantKeys;
}

const StyledTableWrapper: FC<PropsWithChildren<TableWrapperProps>> = styled.div(
  variant({
    scale: 'typography',
  }),
  ({ theme }) => ({
    label: 'tableWrapper',
    // These classes and styles are needed for some legacy code on the
    // discovery pages and may not be needed in the future and may
    // be removed when those are not needed anymore
    '.table-div': {
      display: 'flex',
      alignItems: 'baseline',
      flexWrap: 'wrap',
    },
    table: {
      width: ' 100%',
      margin: `${theme.space.s40}px ${theme.space.s0}`,
      fontSize: '1.1875rem',
      '&.alternating': {
        th: {
          border: 0,
          paddingBottom: theme.space.s0,
        },
        tr: {
          '&:nth-of-type(even)': {
            background: theme.colors.coolGrey[5],
          },
          td: {
            paddingBottom: theme.space.s0,
            borderRight: `2px solid ${theme.colors.white}`,
            '&:nth-of-type(2)': {
              textAlign: 'right',
            },
          },
        },
      },
      '&.small-images': {
        td: {
          img: {
            maxHeight: '80px',
          },
        },
      },
      p: {
        margin: theme.space.s0,
      },
      '.table-div': {
        '> div': {
          width: '100%',
          display: 'inline-block',
        },
        img: {
          width: '100%',
        },
      },
      '.gatsby-image-wrapper': {
        margin: theme.space.s0,
      },
      tr: {
        '&:first-of-type td': {
          paddingTop: theme.space.s16,
        },
      },
      td: {
        padding: `${theme.space.s4}px ${theme.space.s24}px ${theme.space.s24}px ${theme.space.s0}`,
        verticalAlign: 'middle',
        minWidth: '10rem',
        [theme.breakpoints.bp500.max]: {
          minWidth: 0,
        },
      },
      th: {
        textAlign: 'left',
        fontWeight: theme.fontWeights.fw500,
        borderBottom: `1px solid ${theme.colors.coolGrey[90]}`,
        paddingBottom: theme.space.s16,
      },
      '&.symbol-descriptions': {
        tr: {
          verticalAlign: 'top',
        },
        td: {
          padding: theme.space.s0,
          verticalAlign: 'middle',
        },
        '&.small': {
          'td:first-of-type': {
            width: '50px',
          },
        },
        '&.large': {
          'td:first-of-type': {
            width: '80px',
          },
        },
        '&.xlarge td:first-of-type': {
          width: '100px',
        },
        'td:first-of-type': {
          width: '70px',
          paddingRight: theme.space.s16,
        },
      },
    },
  }),
);

const TableWrapper: FC<PropsWithChildren<TableWrapperProps>> = ({ className, variant, children, ...rest }) => (
  <StyledTableWrapper {...rest} className={className} variant={variant}>
    {children}
  </StyledTableWrapper>
);

export { TableWrapper };
export default TableWrapper;
